import { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'

import { useQuery, useQueryClient } from '@tanstack/react-query'
import { t } from 'i18next'

import Loader from '~/components/Loader'
import Panel from '~/components/Panel'
import { isAdvertiser } from '~/helpers'
import { useAppInfo } from '~/hooks/useAppInfo'
import {
  listCampaignSubpublishersKey,
  listCampaignSubpublishersService
} from '~/modules/retailMedia/services/campaign/subpublisher'
import {
  listSubpublisherApprovalStatusKey,
  listSubpublisherApprovalStatusService
} from '~/modules/retailMedia/services/campaign/subpublisher-approval-status'
import {
  getApprovalStatusRefetchInterval,
  shouldPollApprovalStatusForDistribution,
  shouldPollCuratedListForDistribution
} from '~/modules/retailMedia/services/campaign/subpublisher-approval-status/poll'
import { getPublisherDestinationUrls } from '~/modules/retailMedia/store/industry/publisherConfiguration/actions'
import { useAppSelector } from '~/store/hooks'

import EditButton from '../../EditButton'
import DestinationUrlsBody from './elements/Body'
import { DestinationUrlValue } from './elements/Fields'
import {
  composeSubpublisherDestinationSections,
  toCuratedRows
} from './helpers/composeSubpublisherDestinationSections'
import { getDestinationUrlsState } from './helpers/getDestinationUrlsState'

/**
 * Este componente renderiza o painel que gerencia os links de destino de uma campanha.
 * Ele consegue lidar com contas multimarcas e marcas única.
 * Os componentes se alternam nesse caso. Assim como os campos retornados pela API.
 *
 * O componente também conta com dois modos: visualização e edição(desativado).
 * No código tem um bloco para cada modo.
 *
 * @param {object} props.data Dados da campanha
 * @param {boolean} props.loading Indicativo de loading do componente pai
 * @returns
 */

const DestinationUrls = ({
  data,
  loading,
  onPendingValueChange,
  pendingValue,
  isDestinationUrlEnabled = true,
  onDestinationUrlEnabledChange,
  embedded = false
}: {
  data: CampaignView
  loading: boolean
  onPendingValueChange?: (urls: DestinationUrlValue[]) => void
  pendingValue?: DestinationUrlValue[] | null
  isDestinationUrlEnabled?: boolean
  onDestinationUrlEnabledChange?: (value: boolean) => void
  embedded?: boolean
}) => {
  /**
   * Super admin => edição de links de destino
   */
  const { superAdmin } = useAppInfo()

  const [isEditMode, setIsEditMode] = useState(false)

  const {
    industry: {
      publisherConfiguration: {
        listDestinationUrls: {
          data: configurationData,
          loading: configurationLoading
        }
      }
    }
  } = useAppSelector(state => state.retailMedia)

  const isMultiBrand = !!configurationData?.has_brands
  const destinationsConfig = configurationData?.destinations

  const {
    publisherId,
    isSubpublisherReview,
    isPendingReview,
    isRejectedSubpublisherReview,
    showSubpublisherSections,
    pendingReviewMode,
    awaitingDistribution
  } = getDestinationUrlsState({
    data,
    isMultiBrand,
    destinationCount: destinationsConfig?.length
  })

  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  const campaignId = data?.id
  // Redux keeps the previous catalog while the next request is loading, and
  // there is a render where that request has not started yet. Seeding the
  // approval list in that window stores the other campaign's destinations.
  // A global `loading` flag can't tell which campaign's request it belongs
  // to, so settlement is tied to this specific request's own resolution
  // instead: a late response for an already-superseded campaign is ignored,
  // and a failed request never settles (falling back to stale/wrong data).
  const [settledCampaignId, setSettledCampaignId] = useState<string>()
  const pendingCampaignId = useRef<string>()
  const catalogStale = campaignId !== settledCampaignId

  useEffect(() => {
    pendingCampaignId.current = campaignId
    dispatch(
      getPublisherDestinationUrls(publisherId, campaignId, {
        onSuccess: () => {
          if (pendingCampaignId.current === campaignId) {
            setSettledCampaignId(campaignId)
          }
        }
      })
    )
  }, [dispatch, publisherId, campaignId])

  const approvalStatusQueryKey = [listSubpublisherApprovalStatusKey, data.id]
  const allSubpublishersQueryKey = [
    listCampaignSubpublishersKey,
    data.id,
    'all'
  ]

  const getCachedApprovalCount = () =>
    queryClient.getQueryData<{ publishers?: { publisher_id: string }[] }>(
      approvalStatusQueryKey
    )?.publishers?.length ?? 0

  // Same queryKey and envelope (`{ data, total }`) as HandleSubplishers.
  // Returning a bare array here used to crash compose with `.filter is not a function`
  // when the manage panel populated the cache first.
  const { data: curatedSubpublishers } = useQuery({
    queryKey: allSubpublishersQueryKey,
    queryFn: async () => {
      const response = await listCampaignSubpublishersService({
        id: data.id,
        params: { quantity: 1000, count: true }
      })

      return response.data
    },
    enabled: showSubpublisherSections,
    refetchInterval: query =>
      getApprovalStatusRefetchInterval(
        shouldPollCuratedListForDistribution({
          awaitingDistribution,
          curatedCount: toCuratedRows(query.state.data).length,
          approvalCount: getCachedApprovalCount()
        })
      )
  })

  const { data: approvalStatusData, refetch: refetchApprovalStatus } = useQuery(
    {
      queryKey: approvalStatusQueryKey,
      queryFn: async () => {
        const response = await listSubpublisherApprovalStatusService({
          id: data.id
        })

        return response.data
      },
      select: status => status.publishers,
      enabled: showSubpublisherSections,
      refetchInterval: query =>
        getApprovalStatusRefetchInterval(
          shouldPollApprovalStatusForDistribution({
            awaitingDistribution,
            curatedRows: toCuratedRows(curatedSubpublishers),
            approvalPublishers: query.state.data?.publishers
          })
        )
    }
  )

  const canEdit =
    (isSubpublisherReview && !isRejectedSubpublisherReview) ||
    (isAdvertiser() && superAdmin && data?.canEditCampaign)

  // Once approved, a sub-publisher's panel behaves like the advertiser's:
  // read-only by default, editable only via the pencil icon below. Rejected
  // reviews stay read-only even if edit mode was already open before a refetch.
  const effectiveEditMode = pendingReviewMode || (isEditMode && canEdit)

  const subpublishers = useMemo(() => {
    if (!showSubpublisherSections) return []

    return composeSubpublisherDestinationSections({
      curated: toCuratedRows(curatedSubpublishers),
      approvalPublishers: approvalStatusData,
      destinationsConfig
    })
  }, [
    approvalStatusData,
    curatedSubpublishers,
    destinationsConfig,
    showSubpublisherSections
  ])

  const isLoading = loading || configurationLoading || catalogStale
  const body = (
    <DestinationUrlsBody
      data={data}
      isMultiBrand={isMultiBrand}
      destinationsConfig={destinationsConfig}
      catalogLoaded={!configurationLoading && destinationsConfig != null}
      subpublishers={subpublishers}
      effectiveEditMode={effectiveEditMode}
      isDestinationUrlEnabled={isDestinationUrlEnabled}
      onDestinationUrlEnabledChange={onDestinationUrlEnabledChange}
      pendingValue={pendingValue}
      onPendingValueChange={onPendingValueChange}
      onSubpublisherSaved={refetchApprovalStatus}
      onCancelEdit={() => setIsEditMode(false)}
    />
  )

  if (embedded) {
    return isLoading ? <Loader /> : body
  }

  return (
    <Panel
      title={t('rm:adDestination')}
      description={t('rm:adDestination.description')}
      template="shoreline"
      className={effectiveEditMode ? 'border-warning' : undefined}
      headerSideContent={
        canEdit &&
        !showSubpublisherSections &&
        (!isSubpublisherReview || !isPendingReview) && (
          <EditButton
            onClick={() => setIsEditMode((e: boolean) => !e)}
            isActive={isEditMode}
          />
        )
      }
    >
      {isLoading ? <Loader /> : body}
    </Panel>
  )
}

export default DestinationUrls
